/* eslint-disable no-nested-ternary */
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { last, set, uniq, startCase, flatMap, omit } from 'lodash';
import { FinanceType, OfferCategoryType } from '@upstart/harmony/lib/constants';
import { DealerWithFlags } from '@upstart/harmony/lib/legos/dealerTypes';
import { DeskingOffer } from '@upstart/harmony/lib/legos/deskingTypes';
import { sortDeskingOffers } from '@upstart/harmony/lib/desking-utils';
import * as changeLogHelper from '@upstart/harmony/lib/deskingoffer-changelog';
import { isIncentiveProgramExpired } from '@upstart/harmony/lib/incentive-utils';
import { getResidualValueFromPercent } from '@upstart/harmony/lib/payment-utils';
import { Tab, Tabs } from 'react-bootstrap/lib';
import { LeaseOffer } from '@upstart/harmony/lib/legos/paymentTypes';

import useAccessories from 'components/PencilEditor/useAccessories';
import { User } from '@upstart/harmony/lib/legos/userTypes';
import { Car } from '@upstart/harmony/lib/legos/carTypes';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import { Pencil } from '@upstart/harmony/lib/legos/pencilTypes';
import { formatRateWithPrecision } from 'util/format';
import { sendData, getCarInfo } from 'util/amplitude';
import { Action, ModalType, amplitudeEvents } from 'util/amplitudeEvents';
import { isMarketScanIncentivesEnabled } from '@upstart/harmony/lib/dealer-utils';
import { useAutoAnimate } from '../../../hooks/useAutoAnimate';
import { isPresent } from '../../../util/typeUtils';
import TermModal from '../../PencilEditor/PencilOfferList/TermModal';
import { Checkbox } from '../../Inputs/Checkbox';
import DeskingOfferCard from '../DeskingOfferCard';
import DoubleScrollbar from '../../DoubleScrollbar';
import { DeskingOfferError } from '../Validator';
import { Desking, DeskingPayment } from '../types';
import {
  getResidualPercentage,
  calculateAndSaveMoneyFactor,
  updateMonthsMiles,
  getMrm,
  calculateAndSaveRatePercentage,
} from '../DeskingOfferCard/desking-offer-utils';
import { RATE_PRECISION } from '../constants';

const CardLimits: Record<FinanceType, number> = {
  [FinanceType.PURCHASE]: 5,
  [FinanceType.LEASE]: 5,
  [FinanceType.CASH]: 1,
};

function sortOffers(props: DeskingOfferListProps) {
  return (offerA: DeskingOffer, offerB: DeskingOffer) => {
    if (props.displayAllSelected) {
      // Move selected offer left when displaying all offers
      if (offerA.id === props.desking?.acceptedOfferId) {
        return -1;
      }
      if (offerB.id === props.desking?.acceptedOfferId) {
        return 1;
      }
    }

    return sortDeskingOffers(offerA, offerB);
  };
}

export interface DeskingOfferListProps {
  dealer: DealerWithFlags;
  deal: Deal;
  car: Car;
  pencil?: Pencil;
  desking?: Desking;
  deskingPayments?: null | DeskingPayment[];
  errors?: DeskingOfferError[];
  dealerPriceRules: any;
  disabled?: boolean;
  onChange: (offer: DeskingOffer) => Promise<void>;
  updateDeskingOffers: (
    offers: DeskingOffer[],
    params?: {
      computePayments: boolean;
      saveToGlobalState: boolean;
      isMSIncentivesEnabled: boolean;
    },
  ) => Promise<void>;
  onCreateDefaultOffer: (input: {
    dealId: string;
    carId: string;
    deskingId: string;
    financeType: FinanceType;
  }) => void;
  onDuplicateOffer: (offer: Omit<DeskingOffer, 'id'>) => void;
  onRemoveOffer: (offer: DeskingOffer) => void;
  onPushToCRM: (offer: DeskingOffer) => void;
  onPushToCRMDisabled?: boolean;
  onPushToDMS: (offer: DeskingOffer) => void;
  onOpenPencilHistory: (deskingOfferId: string) => void;
  removeExpiredIncentivesRequest?: any;
  displayExpiredIncentiveNotice?: () => void;
  removeUnavailableAccessoriesRequest?: any;
  displayUnavailableAccessoriesNotice?: () => void;
  translations?: any;
  financeType?: FinanceType;
  displayAllSelected?: boolean;
  toggleOnePayLease: () => void;
  deskingOffers: DeskingOffer[] | undefined;
  fetchingDesking?: boolean;
  creatingOffer?: boolean;
  deletingOffer?: boolean;
  currentUser: Partial<User>;
  readOnlyMode: boolean;
  isReadOnlyLoading: boolean;
  selectedIdForPencilHistory?: string;
  isViewingCurrentPencilVersion: boolean;
  sendAmplitudeEvent?: (event: ModalType, action: Action) => void;
}

const matchesFinanceType = (
  offer: DeskingOffer,
  type: FinanceType | undefined,
) => offer.data?.financeType === type;

const matchesCategoryType = (
  offer: DeskingOffer,
  categoryType: OfferCategoryType,
  hasLowInterestHighRebatePurchaseOffers: boolean,
) =>
  !hasLowInterestHighRebatePurchaseOffers ||
  !matchesFinanceType(offer, FinanceType.PURCHASE) ||
  offer.data?.category?.type === categoryType;

const getPurchaseOfferCategories = (allOffers: DeskingOffer[]) =>
  allOffers
    .filter(offer => matchesFinanceType(offer, FinanceType.PURCHASE))
    .map(offer => offer.data?.category?.type)
    .filter(type => !!type);

const DeskingOfferList: FunctionComponent<DeskingOfferListProps> = props => {
  const {
    deal,
    dealer,
    car,
    pencil,
    desking,
    deskingPayments,
    disabled,
    onChange,
    financeType,
    translations,
    removeExpiredIncentivesRequest,
    displayExpiredIncentiveNotice,
    removeUnavailableAccessoriesRequest,
    displayUnavailableAccessoriesNotice,
    dealerPriceRules,
    readOnlyMode,
    isReadOnlyLoading,
    selectedIdForPencilHistory,
    isViewingCurrentPencilVersion,
    sendAmplitudeEvent,
  } = props;

  useEffect(() => {
    let event;
    switch (financeType) {
      case FinanceType.PURCHASE:
        event = amplitudeEvents.DESKING_PENCIL_TAB_FINANCE_START;
        break;
      case FinanceType.CASH:
        event = amplitudeEvents.DESKING_PENCIL_TAB_CASH_START;
        break;
      case FinanceType.LEASE:
        event = amplitudeEvents.DESKING_PENCIL_TAB_LEASE_START;
        break;
      default:
        event = amplitudeEvents.DESKING_PENCIL_TAB_SELECTEDPENCILS_START;
        break;
    }
    if (event) {
      sendData(event, { dealId: deal.id, ...getCarInfo(car) });
    }
  }, [financeType]);

  const [animationParent] = useAutoAnimate<HTMLDivElement>({}, [financeType]);

  const [
    lowInterestHighRebateSelectedOption,
    setLowInterestHighRebateSelectedOption,
  ] = useState<OfferCategoryType>(
    (dealer?.desking?.showSeparateLowInterestHighRebateOffers
      ?.defaultOffer as OfferCategoryType) ?? OfferCategoryType.LOW_INTEREST,
  );

  const [selectedTerm, setSelectedTerm] = useState(null);
  const calledExpireIncentives = useRef(false);
  const calledRemoveAccessories = useRef(false);

  const matchesTerm = (offer: DeskingOffer, term: number) => {
    // Only lease offers have multiple tabs for different terms
    const data = offer.data ?? ({} as DeskingOffer);
    return (
      financeType !== FinanceType.LEASE ||
      ('months' in data && data.months === term)
    );
  };

  const allOffers: DeskingOffer[] =
    props.deskingOffers?.sort(sortOffers(props)) ?? [];
  const presentPurchaseOfferCategories = getPurchaseOfferCategories(allOffers);
  const hasLowInterestHighRebatePurchaseOffers =
    presentPurchaseOfferCategories.length > 0;
  const offerTerms = uniq(
    allOffers
      .filter(offer => offer.data?.financeType === FinanceType.LEASE)
      .map(o => (o.data && 'months' in o.data ? o.data.months : null))
      .filter(isPresent),
  );
  const currentTerm = selectedTerm || offerTerms[0];
  const displayedOffers = allOffers.filter((offer: DeskingOffer) =>
    props.displayAllSelected
      ? offer.isSelectedOffer
      : matchesFinanceType(offer, financeType) &&
        matchesCategoryType(
          offer,
          lowInterestHighRebateSelectedOption,
          hasLowInterestHighRebatePurchaseOffers,
        ) &&
        matchesTerm(offer, currentTerm),
  );

  useEffect(() => {
    // Reselect offer category in case the selected category is no longer present
    if (
      presentPurchaseOfferCategories &&
      !presentPurchaseOfferCategories.includes(
        lowInterestHighRebateSelectedOption,
      )
    ) {
      setLowInterestHighRebateSelectedOption(
        presentPurchaseOfferCategories[0] ?? OfferCategoryType.LOW_INTEREST,
      );
    }
  }, [presentPurchaseOfferCategories, lowInterestHighRebateSelectedOption]);

  useEffect(() => {
    if (!calledExpireIncentives.current && car?.id && deal?.id && pencil?.id) {
      // get all current offers
      const offers = desking?.deskingOffers ?? [];

      // extract all present programs
      const programs = flatMap(offers, offer => offer.data?.programs ?? []);

      // check if any program is expired
      const hasExpired = programs.some(p => isIncentiveProgramExpired(p));
      if (hasExpired) {
        // Only call expire incentives once
        calledExpireIncentives.current = true;
        removeExpiredIncentivesRequest({
          carId: car.id,
          dealId: deal.id,
          pencilId: pencil.id,
          callback: displayExpiredIncentiveNotice,
        });
      }
    }
  }, [
    desking,
    removeExpiredIncentivesRequest,
    displayExpiredIncentiveNotice,
    car,
    deal,
    pencil,
  ]);

  const { accessories, fetching: fetchingAccessories } = useAccessories({
    car: props.car,
    dealer: props.dealer,
    selected: props.deal.selectedAccessories ?? [],
  });
  useEffect(() => {
    if (
      !calledRemoveAccessories.current &&
      car?.id &&
      deal?.id &&
      pencil?.id &&
      !fetchingAccessories
    ) {
      // check if any selected accessory has become unavailable
      const dealHasInvalidAccessories = props.deal.selectedAccessories?.some(
        selectedAccessory =>
          !accessories.find(
            availableAccessory =>
              availableAccessory.id === selectedAccessory.id,
          ),
      );
      const pencilHasInvalidAccessories = props.pencil?.data?.selectedAccessories?.some(
        selectedAccessory =>
          !accessories.find(
            availableAccessory =>
              availableAccessory.id === selectedAccessory.id,
          ),
      );

      if (dealHasInvalidAccessories || pencilHasInvalidAccessories) {
        // Only call remove accessories once
        calledRemoveAccessories.current = true;
        removeUnavailableAccessoriesRequest({
          carId: car.id,
          dealId: deal.id,
          pencilId: pencil.id,
          callback: displayUnavailableAccessoriesNotice,
        });
      }
    }
  }, [
    accessories,
    fetchingAccessories,
    desking,
    removeUnavailableAccessoriesRequest,
    displayUnavailableAccessoriesNotice,
    car,
    deal,
    pencil,
  ]);

  const applyChangesToOffers = (
    offerIdsToUpdate: string[],
    fieldValuePairs: [string, any][],
    shouldLog = false,
  ): DeskingOffer[] => {
    const offersToUpdate = allOffers
      .filter(offer => offerIdsToUpdate.includes(offer.id))
      .map(offer => {
        fieldValuePairs.forEach(([field, value]) => {
          if (shouldLog) {
            let newChangeLog;
            // price overrides are handled unlike the others because it requires an extra parameter
            if (field === 'protectionPlanProductPriceOverrides') {
              newChangeLog = changeLogHelper.updatePriceOverrides(
                offer,
                value,
                dealer?.penProducts ?? [],
              );
            } else {
              newChangeLog = changeLogHelper.updateOfferProperty(
                offer,
                field,
                value,
              );
            }
            set(offer, 'changeLog', newChangeLog);
          }
          set(offer, `sentToSalespersonAt`, null);
          switch (field) {
            case 'months':
              updateMonthsMiles({
                offer,
                months: value,
                mileage: null,
                car,
                dealer,
                dealerPriceRules,
                shouldLog,
                currentUser: props.currentUser,
              });
              break;
            case 'mileage':
              updateMonthsMiles({
                offer,
                months: null,
                mileage: value,
                car,
                dealer,
                dealerPriceRules,
                shouldLog,
                currentUser: props.currentUser,
              });
              break;
            case 'ratePercentage':
              set(offer, `data.${field}`, value);
              calculateAndSaveMoneyFactor({
                offer,
                propName: 'rate',
                newRatePercentage: value,
                oldRatePercentage: (offer.data as LeaseOffer).ratePercentage,
                shouldLog,
                currentUser: props.currentUser,
              });
              break;
            case 'onePayLeaseApr':
              set(offer, `data.${field}`, value);
              calculateAndSaveMoneyFactor({
                offer,
                propName: 'onePayment',
                newRatePercentage: value,
                oldRatePercentage: (offer.data as LeaseOffer).onePayLeaseApr,
                shouldLog,
                currentUser: props.currentUser,
              });
              break;
            case 'rate':
              // matching the same behavior when onBlur event happens when applyChangesToOffers button is pressed
              if (typeof value === 'string') {
                // format rate if no. of decimal digits are greater than 8
                const formattedValue = formatRateWithPrecision(
                  value,
                  RATE_PRECISION,
                );

                // Backend accepts rate type as number or null (this will make sure type will be always number or null)
                value =
                  typeof formattedValue === 'string'
                    ? parseFloat(formattedValue)
                    : typeof formattedValue === 'number'
                    ? formattedValue
                    : null;
              }
              set(offer, `data.${field}`, value);
              calculateAndSaveRatePercentage({
                offer,
                newRate: value,
                oldRate: (offer.data as LeaseOffer).rate,
                shouldLog,
                currentUser: props.currentUser,
              });
              break;
            case 'residual':
              // recalculate residual percentage in other offers when residual value is updated
              set(offer, 'data.residual', value);
              set(
                offer,
                `data.residualPercentage`,
                getResidualPercentage({
                  dealer: props.dealer,
                  car: props.car,
                  offer,
                }),
              );
              break;
            case 'residualPercentage':
              // recalculate residual value in other offers when residual percentage is updated
              set(
                offer,
                'data.residual',
                getResidualValueFromPercent(
                  props.dealer,
                  props.car,
                  getMrm({ car: props.car, offer }),
                  value * 100,
                  offer?.data?.msrp ?? 0,
                ),
              );
              set(offer, `data.residualPercentage`, value);
              break;
            default:
              set(offer, `data.${field}`, value);
          }
        });
        return { ...offer };
      });
    return offersToUpdate;
  };

  const saveOffers = async (
    offersToUpdate: DeskingOffer[],
    saveToGlobalState: boolean = true,
    isMSIncentivesEnabled = false,
  ): Promise<void> => {
    if (offersToUpdate.length) {
      await props.updateDeskingOffers(offersToUpdate, {
        computePayments: true,
        saveToGlobalState,
        isMSIncentivesEnabled,
      });
    }
    return Promise.resolve();
  };

  const allDisplayedOffersSelected = displayedOffers.every(
    offer => offer.isSelectedOffer,
  );
  const isAddOfferDisabled = props.creatingOffer || props.deletingOffer;

  const offerList = (
    <>
      <div className="offer-list-select-all-container">
        {!readOnlyMode && displayedOffers.length > 0 && (
          <Checkbox
            id="select-all-desking-offers"
            label={
              allDisplayedOffersSelected
                ? 'Unselect all offers'
                : 'Select all offers'
            }
            checked={allDisplayedOffersSelected}
            onChange={selectAll => {
              const offersToUpdate = displayedOffers
                .filter(o =>
                  selectAll ? !o.isSelectedOffer : o.isSelectedOffer,
                )
                .map(o => ({ ...o, isSelectedOffer: selectAll }));
              props.updateDeskingOffers(offersToUpdate);
            }}
          />
        )}
      </div>
      <DoubleScrollbar>
        {!financeType && !displayedOffers.length && (
          <h4 className="text-center pt-15">No pencils selected.</h4>
        )}
        <div ref={animationParent} className="offer-list">
          {displayedOffers.map(offer => (
            <DeskingOfferCard
              key={offer.id}
              selected={!!offer.isSelectedOffer}
              accepted={offer.id === desking?.acceptedOfferId}
              hasAcceptedOffer={!!desking?.acceptedOfferId}
              deal={deal}
              dealer={dealer}
              car={car}
              pencil={props.pencil}
              disabled={disabled}
              onChange={onChange}
              offer={offer}
              errors={
                props.errors?.find(error => error.deskingOfferId === offer.id)
                  ?.errors
              }
              offerPayment={
                deskingPayments?.find(
                  payment => payment.deskingOfferId === offer.id,
                )?.payment
              }
              offers={allOffers}
              translations={translations}
              dealerPriceRules={props.dealerPriceRules}
              onPushToCRM={props.onPushToCRM}
              onPushToCRMDisabled={props.onPushToCRMDisabled}
              onPushToDMS={props.onPushToDMS}
              onOpenPencilHistory={props.onOpenPencilHistory}
              onDuplicate={
                financeType && displayedOffers.length < CardLimits[financeType]
                  ? props.onDuplicateOffer
                  : undefined
              }
              onRemove={props.onRemoveOffer}
              saveOffers={saveOffers}
              applyChangesToOffers={applyChangesToOffers}
              displayingAllSelectedOffers={props.displayAllSelected}
              toggleOnePayLease={props.toggleOnePayLease}
              updateDeskingOffers={props.updateDeskingOffers}
              currentUser={props.currentUser}
              readOnlyMode={readOnlyMode}
              isReadOnlyLoading={isReadOnlyLoading}
              isSelectedForPencilHistory={
                selectedIdForPencilHistory === offer.id
              }
              isViewingCurrentPencilVersion={isViewingCurrentPencilVersion}
              sendAmplitudeEvent={sendAmplitudeEvent}
            />
          ))}
          {financeType &&
            displayedOffers.length < CardLimits[financeType] &&
            !readOnlyMode && (
              <div className="offer-container">
                <div className="add-offers-container">
                  <div>Add more offers to this deal.</div>
                  <button
                    disabled={isAddOfferDisabled}
                    className="add-offers-button"
                    type="button"
                    onClick={() => {
                      if (!props.desking) {
                        return;
                      }

                      const lastOffer = last(displayedOffers);

                      // If no offers exist create a default one, otherwise duplicate last offer terms
                      if (lastOffer) {
                        props.onDuplicateOffer({
                          ...omit(lastOffer, ['createdAt', 'updatedAt']),
                          sentToSalespersonAt: null,
                          // Mark new offer as not selected
                          isSelectedOffer: false,
                        });
                      } else {
                        props.onCreateDefaultOffer({
                          dealId: props.deal.id,
                          carId: props.car.id,
                          deskingId: props.desking.id,
                          financeType,
                        });
                      }
                    }}
                  >
                    {isAddOfferDisabled ? 'Loading' : 'Add Offers'}
                  </button>
                </div>
              </div>
            )}
        </div>
      </DoubleScrollbar>
    </>
  );

  if (
    financeType === FinanceType.PURCHASE &&
    hasLowInterestHighRebatePurchaseOffers
  ) {
    return (
      <Tabs
        animation={false}
        id="purchase-low-interest-high-rebate-tabs"
        mountOnEnter
        unmountOnExit
        activeKey={lowInterestHighRebateSelectedOption}
        // @ts-ignore
        onSelect={setLowInterestHighRebateSelectedOption}
      >
        {Object.values(OfferCategoryType).map(type => {
          if (!presentPurchaseOfferCategories.includes(type)) {
            return null;
          }
          return (
            <Tab key={type} eventKey={type} title={startCase(type)}>
              {offerList}
            </Tab>
          );
        })}
      </Tabs>
    );
  }

  if (financeType === FinanceType.LEASE) {
    return (
      <Tabs
        animation={false}
        id="lease-terms-tabs"
        mountOnEnter
        unmountOnExit
        activeKey={currentTerm}
        onSelect={(term: any) => setSelectedTerm(term)}
      >
        {offerTerms.length
          ? offerTerms.map(offerTerm => (
              <Tab
                key={offerTerm}
                eventKey={offerTerm}
                title={
                  <div>
                    {offerTerm} Months{' '}
                    {offerTerm === currentTerm && !readOnlyMode && (
                      <i
                        className="glyphicon glyphicon-pencil"
                        onClick={() => {
                          TermModal({ offerTerm, termList: offerTerms }).then(
                            (term: any) => {
                              if (term) {
                                // Extract applicable offers and their indices
                                const offerIds = displayedOffers
                                  .filter(offer =>
                                    matchesTerm(offer, offerTerm),
                                  )
                                  .map(offer => offer.id);

                                // Update currently selected term
                                setSelectedTerm(term);

                                // Update offers with the new term
                                const updatedOffers = applyChangesToOffers(
                                  offerIds,
                                  [['months', term]],
                                  true,
                                );
                                saveOffers(
                                  updatedOffers,
                                  true,
                                  isMarketScanIncentivesEnabled(dealer),
                                );
                              }
                            },
                          );
                        }}
                      />
                    )}
                  </div>
                }
              >
                {offerList}
              </Tab>
            ))
          : // if no offer terms we should still display offer list - it'll have a single card with "Add Offers" button
            offerList}
      </Tabs>
    );
  }

  return offerList;
};

export default DeskingOfferList;
